import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { useToast } from '../../hooks/useToast';
import {
  emptyDemoArea,
  fetchDemoOverview,
  fillDemoArea,
  type DemoArea,
  type DemoOverview,
} from '../../services/DemoTenantService';

type PendingAction = { area: DemoArea; kind: 'empty' | 'fill' };

/**
 * Administration → Demo areas, only on the Prod demo server: the sales partner empties a demo
 * area after a trial or fills it from a template (docs/CONCEPT_Prod_Demo_Instanz_Hetzner.md, 5.3).
 * Both actions delete data for good, so the name of the area has to be typed to confirm.
 */
const DemoTenantsPage = () => {
  const { t } = useTranslation(['common']);
  const { showToast } = useToast();

  const [overview, setOverview] = useState<DemoOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [templateUuid, setTemplateUuid] = useState<string>('');
  const [confirmName, setConfirmName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOverview(await fetchDemoOverview());
    } catch (err) {
      console.error('Failed to load demo areas', err);
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const start = (area: DemoArea, kind: PendingAction['kind']) => {
    setPending({ area, kind });
    setConfirmName('');
    setError(null);
    setTemplateUuid(overview?.templates[0]?.uuid ?? '');
  };

  const run = async () => {
    if (!pending) return;
    setRunning(true);
    setError(null);
    try {
      const result = pending.kind === 'empty'
        ? await emptyDemoArea(pending.area.uuid, confirmName)
        : await fillDemoArea(pending.area.uuid, templateUuid, confirmName);
      const rows = Object.values(result.rows).reduce((sum, n) => sum + n, 0);
      showToast(
        'success',
        t(`demoTenants.${pending.kind}.successTitle`),
        t(`demoTenants.${pending.kind}.success`, { name: pending.area.name, rows }),
      );
      setPending(null);
      await load();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message || e.message || '');
    } finally {
      setRunning(false);
    }
  };

  const confirmOk = pending !== null && confirmName.trim() === pending.area.name
    && (pending.kind === 'empty' || templateUuid !== '');

  return (
    <div className="p-6 max-w-4xl" data-testid="demo-tenants-page">
      <h1 className="text-2xl font-semibold mb-2">{t('routes.demoTenants')}</h1>
      <p className="text-sm text-gray-600 mb-4">{t('demoTenants.intro')}</p>

      {loading && <div className="text-gray-500">{t('demoTenants.loading')}</div>}
      {!loading && !overview && <div className="text-red-700">{t('demoTenants.loadFailed')}</div>}

      {!loading && overview && (
        <div className="bg-white rounded shadow-sm border mb-6">
          {overview.areas.length === 0 && (
            <div className="p-4 text-gray-500">{t('demoTenants.noAreas')}</div>
          )}
          {overview.areas.map((area) => (
            <div key={area.uuid} className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0">
              <div className="flex-1">
                {area.name}
                {area.current && (
                  <span className="ml-2 text-xs text-gray-500">{t('demoTenants.current')}</span>
                )}
              </div>
              <ButtonComponent
                cssClass="e-outline"
                disabled={area.current || overview.templates.length === 0}
                onClick={() => start(area, 'fill')}
              >
                {t('demoTenants.fill.button')}
              </ButtonComponent>
              <ButtonComponent cssClass="e-outline e-danger" disabled={area.current} onClick={() => start(area, 'empty')}>
                {t('demoTenants.empty.button')}
              </ButtonComponent>
            </div>
          ))}
        </div>
      )}

      {pending && overview && (
        <div className="bg-white rounded shadow-sm border p-4" role="dialog" aria-modal="false">
          <h2 className="text-lg font-semibold mb-2">
            {t(`demoTenants.${pending.kind}.title`, { name: pending.area.name })}
          </h2>
          <p className="text-sm text-gray-700 mb-3">{t(`demoTenants.${pending.kind}.warning`)}</p>
          {pending.kind === 'fill' && (
            <div className="mb-3 max-w-sm">
              <label className="block text-sm text-gray-600 mb-1">{t('demoTenants.fill.template')}</label>
              <DropDownListComponent
                dataSource={overview.templates.map((tpl) => ({ value: tpl.uuid, text: tpl.name }))}
                fields={{ value: 'value', text: 'text' }}
                value={templateUuid}
                change={(e: { value?: string | null }) => setTemplateUuid(e.value ?? '')}
              />
            </div>
          )}
          <label className="block text-sm text-gray-600 mb-1">
            {t('demoTenants.confirmLabel', { name: pending.area.name })}
          </label>
          <input
            className="e-input w-full max-w-sm p-2 border rounded"
            value={confirmName}
            onChange={(e) => { setConfirmName(e.target.value); setError(null); }}
            data-testid="demo-confirm-name"
          />
          {error && <div className="text-red-700 text-sm mt-2" role="alert">{error}</div>}
          <div className="flex gap-2 mt-3">
            <ButtonComponent cssClass="e-outline" onClick={() => setPending(null)} disabled={running}>
              {t('demoTenants.cancel')}
            </ButtonComponent>
            <ButtonComponent cssClass="e-danger" disabled={!confirmOk || running} onClick={run}>
              {t(`demoTenants.${pending.kind}.confirm`)}
            </ButtonComponent>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoTenantsPage;
